import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus } from "lucide-react";

import {
  PoliciesService,
  type PolicyRuleResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Breadcrumb } from "@/components/shared/breadcrumb";
import { PageHeader } from "@/components/shared/page-header";
import { PolicyDeleteDialog } from "@/components/policies/policy-delete-dialog";
import { PolicyVersionDialog } from "@/components/policies/policy-version-dialog";
import { Section } from "@/components/shared/section";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { formatDateID, formatRelativeTimeID } from "@/lib/format";
import { ROUTES } from "@/lib/constants";
import { useNavigate } from "react-router-dom";

const EFFECT_TONE: Record<string, "success" | "warning" | "danger" | "info" | "neutral"> = {
  allow: "success",
  log_only: "neutral",
  redact: "info",
  require_hitl: "warning",
  require_approval: "warning",
  escalate: "warning",
  deny: "danger",
};

// Decision ``effect`` arrives as a plain string from the backend.
// Map the known DSL effects to translation keys; unknown values
// fall back to the raw string.
const EFFECT_LABEL_KEY: Record<string, TranslationKey> = {
  allow: "policies.effects.allow",
  deny: "policies.effects.deny",
  log_only: "policies.effects.logOnly",
  redact: "policies.effects.redact",
  require_approval: "policies.effects.requireApproval",
  require_hitl: "policies.effects.requireHitl",
  escalate: "policies.effects.escalate",
};

export default function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [versionOpen, setVersionOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const resolveEffectLabel = (effect: string): string => {
    const key = EFFECT_LABEL_KEY[effect];
    return key ? t(key) : effect;
  };

  const ruleFetcher = useCallback(() => {
    if (!id) throw new Error(t("policies.page.missingId"));
    return PoliciesService.findById(id);
  }, [id, t]);
  const decisionsFetcher = useCallback(
    () =>
      PoliciesService.listDecisions({
        policy_rule_id: id,
        limit: 20,
      }),
    [id]
  );

  const rule = useApi(ruleFetcher, { deps: [id] });
  const decisions = useApi(decisionsFetcher, { deps: [id] });

  const handleDeleteSuccess = () => {
    setDeleteOpen(false);
    navigate(ROUTES.POLICIES);
  };

  const handleVersionSuccess = () => {
    setVersionOpen(false);
    void rule.refetch();
    void decisions.refetch();
  };

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: t("policies.page.title"), to: ROUTES.POLICIES },
          { label: rule.data?.name ?? t("policies.page.breadcrumbLoading") },
        ]}
      />

      {rule.error && (
        <Alert variant="danger">{parseApiError(rule.error)}</Alert>
      )}

      {rule.isLoading && !rule.data ? (
        <Skeleton className="h-12 w-1/2" />
      ) : rule.data ? (
        <>
          <PageHeader
            title={rule.data.name}
            description={rule.data.description ?? undefined}
            action={
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setVersionOpen(true)} size="sm">
                  <Plus size={14} />
                  {t("policies.actions.newVersion")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                >
                  {t("policies.actions.delete")}
                </Button>
              </div>
            }
          />

          <PolicyMetadata rule={rule.data} />
        </>
      ) : (
        !rule.isLoading && (
          <Alert variant="warning">{t("policies.page.notFound")}</Alert>
        )
      )}

      <Section
        title={t("policies.detail.decisionsTitle")}
        description={t("policies.detail.decisionsDescription")}
      >
        {decisions.error ? (
          <Alert variant="warning">{parseApiError(decisions.error)}</Alert>
        ) : decisions.isLoading && !decisions.data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("policies.detail.colTime")}</TableHead>
                  <TableHead>{t("policies.detail.colEffect")}</TableHead>
                  <TableHead className="hidden tablet:table-cell">
                    {t("policies.detail.colRequestId")}
                  </TableHead>
                  <TableHead className="hidden desktop:table-cell">
                    {t("policies.detail.colUser")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!decisions.data || decisions.data.items.length === 0 ? (
                  <TableEmpty
                    colSpan={4}
                    message={t("policies.detail.decisionsEmpty")}
                  />
                ) : (
                  decisions.data.items.map((decision) => (
                    <TableRow key={decision.id}>
                      <TableCell className="text-neutral-600">
                        {formatRelativeTimeID(decision.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge tone={EFFECT_TONE[decision.effect] ?? "neutral"}>
                          {resolveEffectLabel(decision.effect)}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden font-mono text-xs text-neutral-600 tablet:table-cell">
                        {decision.request_id ?? "—"}
                      </TableCell>
                      <TableCell className="hidden text-neutral-600 desktop:table-cell">
                        {decision.user_id ?? t("policies.detail.systemActor")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      {versionOpen && rule.data && (
        <PolicyVersionDialog
          rule={rule.data}
          initialRule={null}
          onClose={() => setVersionOpen(false)}
          onSuccess={handleVersionSuccess}
        />
      )}

      <PolicyDeleteDialog
        rule={deleteOpen ? rule.data : null}
        onClose={() => setDeleteOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}

function PolicyMetadata({ rule }: { rule: PolicyRuleResponse }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3 rounded-lg border border-ocean-100 bg-white px-4 py-3 text-sm desktop:grid-cols-4">
      <Field label={t("policies.detail.metadataSlug")}>
        <code className="font-mono text-xs text-neutral-600">{rule.slug}</code>
      </Field>
      <Field label={t("policies.detail.metadataPriority")}>
        {rule.priority}
      </Field>
      <Field label={t("policies.detail.metadataStatus")}>
        {rule.is_enabled ? (
          <Badge tone="success">{t("policies.status.enabled")}</Badge>
        ) : (
          <Badge tone="warning">{t("policies.status.disabled")}</Badge>
        )}
      </Field>
      <Field label={t("policies.detail.metadataUpdated")}>
        {formatDateID(rule.updated_at)}
      </Field>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-ocean-900">{children}</div>
    </div>
  );
}
